import {
  CommandGroup,
  CommandInput,
  CommandList,
} from "@volca/ui/components/ui/command";
import { Stack } from "@volca/ui/components/ui/stack";
import type { ApProjectSearchResult } from "@/accounts-payable/types";
import {
  jobSearchStatusMessage,
  projectSearchMeta,
} from "@/accounts-payable/utils";
import { PickerCommand, PickerItem } from "./picker-command";

/** The project twin of `JobSearchCombobox`, for a document filed on a project with no job. */
export const ProjectSearchCombobox = ({
  query,
  onQueryChange,
  projects,
  isLoading,
  isFetching,
  isError,
  isRateLimited,
  selectedProject,
  onSelect,
  disabled,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  projects: ApProjectSearchResult[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isRateLimited?: boolean;
  selectedProject: ApProjectSearchResult | null;
  onSelect: (project: ApProjectSearchResult) => void;
  disabled?: boolean;
}) => {
  const statusMessage = jobSearchStatusMessage({
    query,
    isLoading,
    isError,
    isRateLimited,
    resultCount: projects.length,
    noun: "projects",
  });

  return (
    <Stack className="w-full" align="start" gap={1}>
      <PickerCommand label="Search ServiceTitan projects">
        <CommandInput
          value={query}
          onValueChange={onQueryChange}
          disabled={disabled}
          placeholder="Search by project number, customer, or address"
        />
        <CommandList className="max-h-64">
          {!statusMessage && (
            <CommandGroup>
              {projects.map((project) => (
                <PickerItem
                  key={project.id}
                  value={String(project.id)}
                  selected={selectedProject?.id === project.id}
                  disabled={disabled}
                  onSelect={() => onSelect(project)}
                >
                  <Stack align="start" gap={0} className="min-w-0 flex-1">
                    <Stack
                      direction="row"
                      gap={2}
                      align="baseline"
                      className="flex-wrap"
                    >
                      <span className="font-semibold">
                        Project {project.number}
                      </span>
                      <span
                        className="truncate text-sm text-subtle"
                        title={project.customer_name}
                      >
                        {project.customer_name}
                      </span>
                    </Stack>
                    {project.name && (
                      <span
                        className="truncate text-xs text-subtle"
                        title={project.name}
                      >
                        {project.name}
                      </span>
                    )}
                    {project.address && (
                      <span
                        className="truncate text-xs text-subtle"
                        title={project.address}
                      >
                        {project.address}
                      </span>
                    )}
                    <span className="text-xs text-subtle">
                      {projectSearchMeta(project).join(" · ")}
                    </span>
                  </Stack>
                </PickerItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
        {statusMessage && (
          <Stack
            gap={4}
            align="center"
            role="status"
            className="w-full px-4 py-6 text-center text-sm text-subtle"
          >
            {statusMessage}
          </Stack>
        )}
      </PickerCommand>
      {isFetching && !statusMessage && (
        <span className="text-xs text-subtle">Searching projects…</span>
      )}
    </Stack>
  );
};
